
import { useState } from "react";
import {
  Mail,
  User,
  MessageSquare,
  CheckCircle2,
  AlertCircle,
  Send,
} from "lucide-react";

const INITIAL_STATE = {
  name: "",
  email: "",
  message: "",
};

const Contact = () => {
  const [values, setValues] = useState(INITIAL_STATE);
  const [status, setStatus] = useState("idle");

  const updateField = (field, value) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !values.name.trim() ||
      !values.email.trim() ||
      !values.message.trim()
    ) {
      setStatus("error");
      return;
    }

    setStatus("loading");

    try {
      // Remplacer cette simulation par l'envoi réel via Supabase.
      await new Promise((resolve) => setTimeout(resolve, 600));

      console.log("Form submitted:", values);

      setStatus("success");
      setValues(INITIAL_STATE);
    } catch (error) {
      console.error("Erreur lors de l'envoi :", error);
      setStatus("error");
    }
  };

  const inputClasses =
    "w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10";

  const labelClasses =
    "mb-2 flex items-center gap-2 text-sm font-semibold text-stone-700";

  return (
    <main className="min-h-screen bg-stone-50 px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-6xl">
        {/* En-tête */}
        <div className="mb-10 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800">
            <Mail size={16} />
            Contactez-nous
          </span>

          <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">
            Parlons de votre projet
          </h1>

          <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-stone-500 sm:text-base">
            Une question ou besoin d'informations ? Envoyez-nous un message.
            Nous serons ravis de vous aider.
          </p>
        </div>

        {/* Formulaire */}
        <div className="flex justify-center">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-xl rounded-3xl border border-stone-200 bg-white p-5 shadow-lg shadow-stone-200/50 sm:p-8 lg:p-10"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-stone-900">
                Envoyez-nous un message
              </h2>

              <p className="mt-2 text-sm leading-6 text-stone-500">
                Nous vous répondrons dans les plus brefs délais.
              </p>
            </div>

            {/* Succès */}
            {status === "success" && (
              <div
                role="status"
                className="mb-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
              >
                <CheckCircle2
                  size={20}
                  className="mt-0.5 shrink-0"
                />
                <p>Votre message a bien été envoyé.</p>
              </div>
            )}

            {/* Erreur */}
            {status === "error" && (
              <div
                role="alert"
                className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                <AlertCircle
                  size={20}
                  className="mt-0.5 shrink-0"
                />
                <p>
                  Veuillez remplir tous les champs avant d'envoyer
                  votre message.
                </p>
              </div>
            )}

            {/* Nom */}
            <div className="mb-5">
              <label
                htmlFor="contact-name"
                className={labelClasses}
              >
                <User size={18} className="text-amber-600" />
                Nom
              </label>

              <input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                placeholder="Votre nom"
                value={values.name}
                onChange={(e) => updateField("name", e.target.value)}
                required
                className={inputClasses}
              />
            </div>

            {/* Email */}
            <div className="mb-5">
              <label
                htmlFor="contact-email"
                className={labelClasses}
              >
                <Mail size={18} className="text-amber-600" />
                Adresse e-mail
              </label>

              <input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="vous@exemple.com"
                value={values.email}
                onChange={(e) => updateField("email", e.target.value)}
                required
                className={inputClasses}
              />
            </div>

            {/* Message */}
            <div className="mb-6">
              <label
                htmlFor="contact-message"
                className={labelClasses}
              >
                <MessageSquare
                  size={18}
                  className="text-amber-600"
                />
                Message
              </label>

              <textarea
                id="contact-message"
                name="message"
                rows={5}
                placeholder="Comment pouvons-nous vous aider ?"
                value={values.message}
                onChange={(e) => updateField("message", e.target.value)}
                required
                className={`${inputClasses} min-h-36 resize-y rounded-2xl`}
              />
            </div>

            {/* Bouton d'envoi */}
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-6 py-3.5 font-bold text-stone-950 shadow-md transition duration-200 hover:-translate-y-0.5 hover:bg-amber-400 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-stone-900/30 border-t-stone-900" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Envoyer le message
                </>
              )}
            </button>

            <p className="mt-4 text-center text-xs leading-5 text-stone-400">
              Vos informations sont utilisées pour répondre à votre demande.
            </p>
          </form>
        </div>
      </div>
    </main>
  );
};

export default Contact;
